import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { makeStyles, FormControl, Theme, Box } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { uniq } from 'lodash';
import moment from 'moment';
import DatePicker from '../../../components/DatePicker';
import { DropDownField, LicenseListFiltersType, LicenseTypeListType } from '../../../store/modules/application/types';
import { LICENSE_STATUS } from '../../../store/app.constants';
import { useAuthenticationContext } from '../../../authentication';
import { siteActions } from '../../../store/actions';
import { Api } from '../../../store/services/services';
import AutoCompleteSelect from '../../../components/AutoCompleteSelect';

type LicenseFiltersProps = {
    listFiltersRefresher: string;
    listFilters: LicenseListFiltersType;
    changeLicenseFilters: Function;
    applyFilters: Function;
    licenseTypes: LicenseTypeListType;
    numberOfRows: number;
    setPageNumber: Function;
    checkIsLicenseFiltersApplied: Function;
    orderBy: string;
    orderCol: string;
    DEFAULT_SORT_ORDER: string;
    DEFAULT_SORT_COL: string;
    setOrderBy: Function;
    setOrderCol: Function;
    licensesSort: { orderBy: string; orderCol: string };
};

const LicenseFilters: FunctionComponent<LicenseFiltersProps> = (props) => {
    const dispatch = useDispatch();
    const authContext = useAuthenticationContext();
    const getAccessToken = authContext.getAccessToken;
    const scopes = authContext.scopes;

    const {
        listFilters,
        changeLicenseFilters,
        applyFilters,
        licenseTypes,
        numberOfRows,
        setPageNumber,
        checkIsLicenseFiltersApplied,
        DEFAULT_SORT_ORDER,
        DEFAULT_SORT_COL,
        setOrderBy,
        setOrderCol,
        licensesSort,
    } = props;

    const [statusList, setStatusList] = useState<string[]>([]);

    const getStatusList = useCallback(async () => {
        try {
            const allStatuses: string[] = [];

            // Filter statuses by the selected license types.  Use all license types if none are selected.
            const selectedLicenseTypes = listFilters.licenseType.length > 0 ? listFilters.licenseType : licenseTypes;
            const workflowIds = uniq(selectedLicenseTypes.flatMap((licType) => licType.workflowId ?? []));

            if (workflowIds.length) {
                const token = await getAccessToken({ scopes: [scopes['workflows.read']] });
                const workflows = await Api.getWorkflows(token.accessToken, workflowIds);

                allStatuses.push(...workflows.flatMap((workflow) => workflow.states).flatMap((state) => state.name));
            }

            if (selectedLicenseTypes.find((licType) => !licType.workflowId)) {
                // For license types without a workflow, add the default set of statuses.
                allStatuses.push(...LICENSE_STATUS);
            }

            setStatusList(uniq(allStatuses.sort()));
        } catch (err) {
            dispatch(siteActions.setError(err));
        }
    }, [licenseTypes, listFilters.licenseType, dispatch, getAccessToken, scopes]);

    useEffect(() => {
        getStatusList();
    }, [getStatusList]);

    useEffect(() => {
        if (statusList.length && listFilters.licenseStatus?.length > 0) {
            const selectedStatuses = listFilters.licenseStatus.filter((status) => statusList.includes(status));

            // If some previously selected statuses are no longer valid, remove them from listFilters.
            if (selectedStatuses.length !== listFilters.licenseStatus.length) {
                changeLicenseFilters({ ...listFilters, licenseStatus: selectedStatuses });
            }
        }
    }, [statusList, listFilters, changeLicenseFilters]);

    const classes = makeStyles((theme: Theme) => ({
        filtersContainer: {
            width: '100%',
            maxHeight: 300,
            color: 'gray',
            paddingTop: '10px',
            paddingBottom: '10px',
        },
        formControl: {
            marginLeft: 15,
            minWidth: 160,
            maxWidth: 160,
        },
        filtersExport: {
            color: theme.palette.primary.light,
        },
        underline: {
            '&:before': {
                borderBottomColor: 'white',
            },
            '&:after': {
                borderBottomColor: 'white',
            },
            '&:hover:before': {
                borderBottomColor: ['white', '!important'],
            },
        },
        datePickerContainer: {
            backgroundColor: 'white',
            borderRadius: 5,
        },
        clearFiltersBtn: {
            padding: '0px',
            fontWeight: 500,
            marginLeft: 25,
            'text-transform': 'initial',
            cursor: 'pointer',
            color: theme.palette.primary.main,
            marginTop: 22,
        },
        autoCompleteInput: {
            width: '130px !important',
        },
        autoCompleteInputRoot: {
            width: '165px',
            maxHeight: '55px',
            overflow: 'hidden',
            display: 'inline-block',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        autoCompleteEndAdornment: {
            top: 'calc(50% - 14px)',
            right: 2,
            position: 'absolute',
        },
        autoCompletePaper: {
            width: '200px',
        },
        autoCompleteRoot: {
            marginTop: '3px !important',
        },
    }))();
    const handleMultiSelectChange = (key: string, dropDownData: DropDownField[]) => {
        let data;
        const selectedValues = dropDownData.map((obj: DropDownField) => obj.value);
        if (key === 'licenseType') {
            const selectedIds = selectedValues;
            const types: LicenseTypeListType = licenseTypes.filter((type) => {
                return selectedIds.indexOf(type.id) !== -1;
            });
            data = { ...listFilters, [key]: types };
        } else {
            data = { ...listFilters, [key]: selectedValues };
        }
        changeLicenseFilters(data);
        const momentToDate = moment(data.toDate);
        const momentFromDate = moment(data.fromDate);
        if (momentToDate.isValid() && momentFromDate.isValid() && momentToDate.diff(momentFromDate, 'days') >= 0) {
            applyFilters(data, {
                pageNumber: 0,
                numberOfRows,
                status: false,
                orderBy: licensesSort?.orderBy ? licensesSort.orderBy : DEFAULT_SORT_ORDER,
                orderCol: licensesSort?.orderCol ? licensesSort.orderCol : DEFAULT_SORT_COL,
            });
            setPageNumber(0);
            setOrderBy(licensesSort?.orderBy ? licensesSort.orderBy : DEFAULT_SORT_ORDER);
            setOrderCol(licensesSort?.orderCol ? licensesSort.orderCol : DEFAULT_SORT_COL);
        } else if (data.toDate === '' && data.fromDate === '') {
            applyFilters(data, {
                pageNumber: 0,
                numberOfRows,
                status: false,
                orderBy: licensesSort?.orderBy ? licensesSort.orderBy : DEFAULT_SORT_ORDER,
                orderCol: licensesSort?.orderCol ? licensesSort.orderCol : DEFAULT_SORT_COL,
            });
            setPageNumber(0);
            setOrderBy(licensesSort?.orderBy ? licensesSort.orderBy : DEFAULT_SORT_ORDER);
            setOrderCol(licensesSort?.orderCol ? licensesSort.orderCol : DEFAULT_SORT_COL);
        }
    };

    const handleDateChange = (key: string, value: string) => {
        let data = { ...listFilters, [key]: value as string };
        changeLicenseFilters(data);
        applyFilters(data, {
            pageNumber: 0,
            numberOfRows,
            status: false,
            orderBy: licensesSort?.orderBy ? licensesSort.orderBy : DEFAULT_SORT_ORDER,
            orderCol: licensesSort?.orderCol ? licensesSort.orderCol : DEFAULT_SORT_COL,
        });
        setPageNumber(0);
        setOrderBy(licensesSort?.orderBy ? licensesSort.orderBy : DEFAULT_SORT_ORDER);
        setOrderCol(licensesSort?.orderCol ? licensesSort.orderCol : DEFAULT_SORT_COL);
    };

    const resetFilters = () => {
        const data = {
            licenseType: [],
            licenseStatus: [],
            fromDate: '',
            toDate: '',
        };
        changeLicenseFilters(data);
        applyFilters(data, { pageNumber: 0, numberOfRows, status: false });
        setPageNumber(0);
    };

    return (
        <Box width="100%" display="flex" justifyContent="space-between">
            <Box width="70%" display="flex" flexWrap="wrap" alignItems="center" className={classes.filtersContainer}>
                <Box>
                    <FormControl className={classes.formControl}>
                        <AutoCompleteSelect
                            id="license-types-multiple-checkbox"
                            size="small"
                            inputSize={'small'}
                            variant={'standard'}
                            label={'License Type'}
                            isMultipleValue={true}
                            disableCloseOnSelect={true}
                            disableClearable={true}
                            data={licenseTypes?.map((type) => ({
                                label: type.name,
                                value: type.id,
                            }))}
                            classes={{
                                root: classes.autoCompleteRoot,
                                input: classes.autoCompleteInput,
                                inputRoot: classes.autoCompleteInputRoot,
                                endAdornment: classes.autoCompleteEndAdornment,
                                paper: classes.autoCompletePaper,
                            }}
                            onChange={(data: DropDownField[]) => {
                                handleMultiSelectChange('licenseType', data);
                            }}
                            value={listFilters.licenseType.map((license) => ({
                                label: license.name,
                                value: license.id,
                            }))}
                        />
                    </FormControl>
                </Box>
                <Box>
                    <FormControl className={classes.formControl}>
                        <AutoCompleteSelect
                            id="status-multiple-checkbox"
                            label={'Status'}
                            size="small"
                            inputSize={'small'}
                            variant={'standard'}
                            isMultipleValue={true}
                            disableCloseOnSelect={true}
                            disableClearable={true}
                            data={statusList?.map((status) => ({
                                label: status,
                                value: status,
                            }))}
                            classes={{
                                root: classes.autoCompleteRoot,
                                input: classes.autoCompleteInput,
                                inputRoot: classes.autoCompleteInputRoot,
                                endAdornment: classes.autoCompleteEndAdornment,
                                paper: classes.autoCompletePaper,
                            }}
                            value={listFilters.licenseStatus.map((status) => ({
                                label: status,
                                value: status,
                            }))}
                            onChange={(data: DropDownField[]) => {
                                handleMultiSelectChange('licenseStatus', data);
                            }}
                        />
                    </FormControl>
                </Box>
                <Box>
                    <FormControl className={classes.formControl}>
                        <DatePicker
                            containerClasses={classes.datePickerContainer}
                            value={listFilters.fromDate}
                            maxDate={moment().format('YYYY-MM-DD')}
                            maxDateMessage="Date cannot be in the future."
                            handleDate={(date: string) => {
                                const momentDate = moment(date);
                                if (momentDate.isValid() && momentDate.get('years') >= 1900) {
                                    handleDateChange('fromDate', momentDate.format('YYYY-MM-DD'));
                                }
                                if (date === '') {
                                    handleDateChange('fromDate', '');
                                }
                            }}
                            fullWidth={true}
                            label="From Date"
                        ></DatePicker>
                    </FormControl>
                </Box>
                <Box>
                    <FormControl className={classes.formControl}>
                        <DatePicker
                            containerClasses={classes.datePickerContainer}
                            value={listFilters.toDate}
                            handleDate={(date: string) => {
                                const momentDate = moment(date);
                                if (momentDate.isValid()) {
                                    handleDateChange('toDate', momentDate.format('YYYY/MM/DD'));
                                }
                                if (date === '') {
                                    handleDateChange('toDate', '');
                                }
                            }}
                            minDate={listFilters.fromDate || undefined}
                            minDateMessage="Cannot be lesser than from date"
                            fullWidth={true}
                            label="To Date"
                        ></DatePicker>
                    </FormControl>
                </Box>
                <Box display="flex" flexDirection="column">
                    {checkIsLicenseFiltersApplied() && (
                        <div onClick={resetFilters} className={classes.clearFiltersBtn} color="primary">
                            Clear Filters
                        </div>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
export default LicenseFilters;
